import {
  forwardRef,
  useRef,
  useState,
} from "react";
import TextArea from "../old-ui/TextArea";
import EmojiPickerPopover from "./EmojiPickerPopover";
import EmojiTriggerIcon from "./EmojiTriggerIcon";
import { insertEmojiAtCursor, notifyFieldChange, toFieldString } from "./insertEmojiAtCursor";
import { useEmojiPickerDismiss } from "./useEmojiPickerDismiss";
import "./emojiFlagPolyfill.css";

interface EmojiTextAreaProps {
  name: string;
  display: string;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  required?: boolean;
  error?: string;
  touched?: boolean;
  reasonHeaderNeeded?: boolean;
  value?: string;
  onChange?: (event: any) => void;
  onBlur?: () => void;
}

const EmojiTextArea = forwardRef(
  (
    {
      name,
      display,
      placeholder,
      readOnly = false,
      maxLength = 200,
      required = false,
      error,
      touched,
      reasonHeaderNeeded = true,
      value = "",
      onChange,
      onBlur,
    }: EmojiTextAreaProps,
    ref: any,
  ) => {
    const [showPicker, setShowPicker] = useState(false);
    const textAreaRef = useRef(null);
    const triggerRef = useRef(null);
    const pickerRef = useRef(null);

    useEmojiPickerDismiss(showPicker, () => setShowPicker(false), triggerRef, pickerRef);

    const setRefs = (node: HTMLTextAreaElement | null) => {
      textAreaRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const handleEmojiSelect = (emoji: string) => {
      const textArea = textAreaRef.current as HTMLTextAreaElement | null;
      const currentText = toFieldString(value);
      const { value: nextValue, cursorPosition } = insertEmojiAtCursor(
        currentText,
        emoji,
        textArea?.selectionStart ?? null,
        textArea?.selectionEnd ?? null,
      );

      notifyFieldChange(onChange, nextValue);

      setShowPicker(false);

      requestAnimationFrame(() => {
        if (textArea) {
          textArea.focus();
          textArea.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    };

    return (
      <div className="relative emoji-flag-polyfill">
        <TextArea
          ref={setRefs}
          name={name}
          display={display}
          placeholder={placeholder}
          readOnly={readOnly}
          maxLength={maxLength}
          required={required}
          error={error}
          touched={touched}
          reasonHeaderNeeded={reasonHeaderNeeded}
          value={toFieldString(value)}
          onChange={onChange}
          onBlur={onBlur}
        />

        {!readOnly && (
          <>
            <button
              ref={triggerRef}
              type="button"
              className="absolute right-2 top-2 z-10 flex items-center justify-center text-gray-500 hover:text-gray-700"
              onClick={() => setShowPicker((open: boolean) => !open)}
              aria-label="Insert emoji"
              aria-expanded={showPicker}
            >
              <EmojiTriggerIcon />
            </button>
            {showPicker && (
              <EmojiPickerPopover
                anchorEl={triggerRef.current}
                onSelect={handleEmojiSelect}
                onClose={() => setShowPicker(false)}
                pickerRef={(node) => {
                  pickerRef.current = node;
                }}
              />
            )}
          </>
        )}
      </div>
    );
  },
);

EmojiTextArea.displayName = "EmojiTextArea";

export default EmojiTextArea;
